/**
 * app-bar.js
 */

import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import {
  useMediaQuery,
  AppBar,
  Box,
  Badge,
  IconButton,
  Toolbar,
  Typography,
  Alert,
  Button,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NotificationsContext from 'contexts/notifications/notifications.context';
import { UserContext } from 'contexts/user/user-context';
import { AppBarContext } from 'contexts/app-bar/app-bar-context';
import Icon from 'components/icon';
import DropMenu from 'components/drop-menu';
import AccountMenu from './account-menu';
import HelpMenu from './help-menu';
import NotificationsButton from './notifications-button';
import RoleMenu from './role-menu';
import ThemePicker from './theme-picker';
import logoBadge from 'assets/images/logo-badge.png';


const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.comp.navBar.background,
    color: theme.palette.sys.onPrimary,
    boxShadow: 'none',
  },
  toolBar: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    margin: '0 auto',
  },
  title: {
    display: 'flex',
    overflow: 'hidden'
  },
  mobileTitle: {
    marginLeft: theme.spacing(1),
    fontSize: '1rem',
    textTransform: 'uppercase',
    textAlign: 'left',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  homeLink: {
    textDecoration: 'none',
    color: 'inherit',
  },
  logoBadge: {
    height: 38,
    width: 28,
    marginRight: theme.spacing(1.5),
    [theme.breakpoints.down('lg')]: {
      display: 'none',
    }
  },
  logoText: {
    fontFamily: 'good_timing_bd',
    fontSize: '28px',
    letterSpacing: '-0.05em',
    paddingBottom: theme.spacing(0.5),
    [theme.breakpoints.down('sm')]: {
      fontSize: '20px',
    },
  },
  menuButton: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.up('lg')]: {
      display: 'none',
    },
    [theme.breakpoints.down('sm')]: {
      marginRight: 0,
    }
  },
  mobileActions: {
    position: 'relative',
    right: -10,
  },
  appBarShortcuts: {
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    }
  },
}));


export default function PTAppBar(props) {
  const classes = useStyles();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });
  const { user, admin, impersonateUser } = React.useContext(UserContext);
  const { unreadCount } = React.useContext(NotificationsContext);
  const { appBarTitle, appBarActions } = React.useContext(AppBarContext);


  return <>
    <AppBar position='fixed' className={classes.root}>
      <Toolbar className={classes.toolBar}>
        <IconButton
          color="inherit"
          aria-label="open main menu"
          edge="start"
          onClick={props.drawerToggle}
          className={classes.menuButton}
          size="large">
          <Badge
            color="secondary"
            variant="dot"
            invisible={!isMobile || unreadCount === 0}
            children={<Icon type='menu-burger' />}
          />
        </IconButton>

        {/* TITLE */}
        <div className={classes.title}>
          {isMobile && appBarTitle
            ? (
              <Typography variant="h6" className={classes.mobileTitle} data-testid='mobile-appbar-title'>
                {appBarTitle}
              </Typography>
            )
            : (
              <RouterLink to='/' className={`flex-hc ${classes.homeLink}`}>
                <img src={logoBadge} className={classes.logoBadge} alt="PACKTRACK Badge Logo" />
                <div className={classes.logoText}>PACKTRACK</div>
              </RouterLink>
            )}
        </div>
        
        {/* IMPERSONATION INFO */}
        <Box sx={{ flex: 1, display: 'flex', justifyContent: isMobile ? 'flex-end' : 'center' }}>
          {user && admin && user.id !== admin.id && !isMobile &&
          <Alert severity='info' icon={<Icon type='impersonate' />} sx={{ display: 'inline-flex', alignItems: 'center', py: 0 }}>
            You are impersonating {user.name}
            <Button 
              size='small' 
              variant='outlined' 
              sx={{ ml: 2 }} 
              onClick={async () => {
                if (await impersonateUser(admin.id)) {
                  window.location = `/admin/user/${user.id}`;
                }
              }}
              children='Stop'
            />
          </Alert>}
          {user && admin && user.id !== admin.id && isMobile &&
          <DropMenu
            id='stop-impersonating-mobile'
            items={[{ 
              key: 'stop', 
              name: `Stop Impersonating ${user.name}`, 
              onSelect: async () => {
                if (await impersonateUser(admin.id)) {
                  window.location = `/admin/user/${user.id}`;
                }
              }
            }]}
            label={<IconButton color='inherit'>
              <Icon type='impersonate' />
            </IconButton>}
          />}
        </Box>

        {/* MOBILE ACTIONS */}
        {isMobile && appBarActions &&
        <div className={classes.mobileActions}>
          {appBarActions.map((action, i) => (
            <span key={i}>{action}</span>
          ))}
        </div>}

        {/* DESKTOP ACTIONS */}
        {!isMobile &&
        <div className={classes.appBarShortcuts}>
          <HelpMenu />
          <NotificationsButton />
          <ThemePicker />
          <RoleMenu />
          <AccountMenu />
        </div>}
      </Toolbar>
    </AppBar>
  </>;
}